/**
 * Company Configuration & Multi-Tenant Branding Engine
 * 
 * Supports dynamic switching between Eko Power Limited and EKEDP (Eko Electricity Distribution PLC)
 * based on domain hostname, URL parameter (?company=ekedp), or environment variable (REACT_APP_COMPANY).
 */

export type CompanyId = 'ekopower' | 'ekedp';

export interface CompanyConfig {
  id: CompanyId;
  companyName: string;
  shortName: string;
  tagline: string;
  portalTitle: string;
  logo: string;
  favicon: string;
  faviconIco: string;
  website: string;
  emailDomain: string;
  itSupportText: string;
  copyright: string;
  termsText: string;
  cardOrgName: string;
  domains: string[];
  requiredGroupId: string;
  counterpartId: CompanyId;
  counterpartPortalName: string;
  // Accent & theme highlight tokens
  colors: {
    primary: string;
    primaryDark: string;
    primaryLight: string;
    accent: string;
    accentSecondary: string;
    heroGlow: string;
    heroBg: string;
    buttonGradient: string;
    buttonHoverGradient: string;
  };
}

export const COMPANIES: Record<CompanyId, CompanyConfig> = {
  ekopower: {
    id: 'ekopower',
    companyName: 'Eko Power Limited',
    shortName: 'Eko Power',
    tagline: 'Digital Identity Platform',
    portalTitle: 'Eko Power Digital ID',
    logo: '/branding/ekopower/logo.png',
    favicon: '/branding/ekopower/favicon.png',
    faviconIco: '/branding/ekopower/favicon.ico',
    website: 'https://ekopowerlimited.com',
    emailDomain: 'ekopowerlimited.com',
    itSupportText: "Powered by Eko Power IT & System's Function",
    copyright: `© ${new Date().getFullYear()} Eko Power Limited. All rights reserved.`,
    termsText: "By signing in you agree to Eko Power Limited's Terms of Use",
    cardOrgName: 'Eko Power Limited',
    domains: ['ekopower', 'ekopowerlimited.com'],
    requiredGroupId: '2062e130-258d-4876-ac65-9fdbb2963bf1',
    counterpartId: 'ekedp',
    counterpartPortalName: 'EKEDP Digital Identity Portal',
    colors: {
      primary: '#1565C0',
      primaryDark: '#0D1F4C',
      primaryLight: '#5A99F7',
      accent: '#FFB703',
      accentSecondary: '#00B4D8',
      heroGlow: 'rgba(99, 140, 255, 0.18)',
      heroBg: '#0B1B3D',
      buttonGradient: 'linear-gradient(135deg, #091A42 0%, #10429D 100%)',
      buttonHoverGradient: 'linear-gradient(135deg, #0C235A 0%, #1555C8 100%)',
    },
  },
  ekedp: {
    id: 'ekedp',
    companyName: 'Eko Electricity Distribution PLC',
    shortName: 'EKEDP',
    tagline: 'Corporate Digital Identity',
    portalTitle: 'EKEDP Digital ID',
    logo: '/branding/ekedp/logo.png',
    favicon: '/branding/ekedp/favicon.png',
    faviconIco: '/branding/ekedp/favicon.ico',
    website: 'https://ekedp.com',
    emailDomain: 'ekedp.com',
    itSupportText: "Powered by EKEDP IT & System's Function",
    copyright: `© ${new Date().getFullYear()} Eko Electricity Distribution PLC. All rights reserved.`,
    termsText: "By signing in you agree to EKEDP's Terms of Use",
    cardOrgName: 'Eko Electricity Distribution PLC',
    domains: ['ekedp', 'ekedc', 'ekedp.com'],
    requiredGroupId: 'e1802ab5-b821-4334-9e84-91b9649b8cf0',
    counterpartId: 'ekopower',
    counterpartPortalName: 'Eko Power Digital Identity Portal',
    colors: {
      primary: '#312E60',
      primaryDark: '#1C1844',
      primaryLight: '#6E66B8',
      accent: '#EC3138',
      accentSecondary: '#FFDC21',
      heroGlow: 'rgba(236, 49, 56, 0.16)',
      heroBg: '#13102C',
      buttonGradient: 'linear-gradient(135deg, #1C1844 0%, #312E60 100%)',
      buttonHoverGradient: 'linear-gradient(135deg, #26215A 0%, #3E3B78 100%)',
    },
  },
};

/**
 * Resolves active company ID from:
 * 1. URL Query Param (?company=ekedp or ?company=ekopower)
 * 2. Hostname matching (e.g. ekedp.com or ekopowerlimited.com)
 * 3. Environment Variable (REACT_APP_COMPANY)
 * 4. Default fallback ('ekopower')
 */
export const resolveCurrentCompanyId = (): CompanyId => {
  if (typeof window !== 'undefined') {
    // 1. Explicit URL Query parameter override (for manual testing e.g. ?company=ekedp)
    const params = new URLSearchParams(window.location.search);
    const paramCompany = params.get('company')?.toLowerCase() as CompanyId;
    if (paramCompany && COMPANIES[paramCompany]) {
      return paramCompany;
    }

    // 2. Production Domain / Hostname detection (takes absolute priority over cached local storage)
    const hostname = window.location.hostname.toLowerCase();
    if (COMPANIES.ekedp.domains.some((d) => hostname.includes(d))) {
      return 'ekedp';
    }
    if (COMPANIES.ekopower.domains.some((d) => hostname.includes(d))) {
      return 'ekopower';
    }

    // 3. Localhost / Preview environments fallback: check localStorage
    const saved = localStorage.getItem('app-company-override') as CompanyId;
    if (saved && COMPANIES[saved]) {
      return saved;
    }
  }

  // 4. Environment variable
  const envCompany = process.env.REACT_APP_COMPANY?.toLowerCase() as CompanyId;
  if (envCompany && COMPANIES[envCompany]) {
    return envCompany;
  }

  // 5. Default fallback
  return 'ekopower';
};

/**
 * Dynamically updates document title and favicon links in DOM head
 */
export const applyCompanyMetadata = (company: CompanyConfig) => {
  if (typeof document === 'undefined') return;

  // 1. Update Title
  document.title = `${company.shortName} - Digital Business Card`;

  // 2. Update Favicon & Apple Touch Icon
  const updateLink = (rel: string, href: string, type?: string) => {
    let link = document.querySelector(`link[rel*='${rel}']`) as HTMLLinkElement;
    if (!link) {
      link = document.createElement('link');
      link.rel = rel;
      document.head.appendChild(link);
    }
    link.href = href;
    if (type) link.type = type;
  };

  updateLink('icon', company.favicon, 'image/png');
  updateLink('alternate icon', company.faviconIco);
  updateLink('apple-touch-icon', company.favicon);

  // 3. Update theme-color meta tag
  let themeMeta = document.querySelector("meta[name='theme-color']") as HTMLMetaElement;
  if (themeMeta) {
    themeMeta.content = company.colors.primaryDark;
  }
};
